import { RefreshCw, TrendingUp, Trash2, Lightbulb } from 'lucide-react';
import type { TabEvolutionUpdates, DifficultyLevel } from '../../../../types/craft';

interface Props {
  data: TabEvolutionUpdates;
  quarterLabel: string;
  version: number;
}

const CLASSIFICATION_STYLES = {
  new: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  updated: { bg: 'bg-app-focus/10', text: 'text-app-focus' },
  deprecated: { bg: 'bg-app-critical/10', text: 'text-app-critical' },
};

const IMPACT_STYLES = {
  high: { bg: 'bg-app-critical/10', text: 'text-app-critical' },
  medium: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
  low: { bg: 'bg-app-canvas', text: 'text-app-muted' },
};

export default function EvolutionUpdatesTab({ data, quarterLabel, version }: Props) {
  const hasData = data?.changes_this_quarter?.length || data?.emerging_practices?.length || data?.deprecated_advice?.length;

  if (!hasData) {
    return (
      <div className="text-center py-16">
        <RefreshCw className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted font-medium mb-1">Evolution Engine</p>
        <p className="text-xs text-app-muted max-w-md mx-auto">
          The evolution engine compares previous and current intelligence to detect changes, emerging practices, and deprecated advice. It will populate after the first quarterly refresh (version 2+).
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-app-muted">
          <span>Current: {quarterLabel || 'N/A'}</span>
          <span>Version: {version}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-app-surface border border-app-line rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw className="w-5 h-5 text-app-muted" />
          <h3 className="text-sm font-bold text-app-ink">Quarterly Evolution Report</h3>
        </div>
        <p className="text-xs text-app-muted">
          {quarterLabel}, version {version}. Compared with the previous quarter.
        </p>
        {(data.confidence_score > 0 || data.recency_score > 0) && (
          <div className="flex items-center gap-4 mt-3">
            {data.confidence_score > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-app-muted">Confidence:</span>
                <div className="w-16 h-1 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label="Confidence" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(data.confidence_score * 100)}>
                  <div className="h-full bg-app-focus rounded-full" style={{ width: `${data.confidence_score * 100}%` }} />
                </div>
                <span className="text-[10px] text-app-muted">{Math.round(data.confidence_score * 100)}%</span>
              </div>
            )}
            {data.recency_score > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-app-muted">Recency:</span>
                <div className="w-16 h-1 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label="Recency" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(data.recency_score * 100)}>
                  <div className="h-full bg-app-positive rounded-full" style={{ width: `${data.recency_score * 100}%` }} />
                </div>
                <span className="text-[10px] text-app-muted">{Math.round(data.recency_score * 100)}%</span>
              </div>
            )}
          </div>
        )}
      </div>

      {data.changes_this_quarter?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Changes This Quarter</h4>
          <div className="space-y-2">
            {data.changes_this_quarter.map((c, i) => {
              const cls = CLASSIFICATION_STYLES[c.classification] || CLASSIFICATION_STYLES.updated;
              const imp = IMPACT_STYLES[c.impact_level] || IMPACT_STYLES.medium;
              return (
                <div key={i} className="bg-app-surface border border-app-line rounded-lg p-3 flex items-start gap-3">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 mt-0.5 ${cls.bg} ${cls.text}`}>
                    {c.classification}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm text-app-ink">{c.change}</p>
                    {c.rationale && (
                      <p className="text-xs text-app-muted mt-1">{c.rationale}</p>
                    )}
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${imp.bg} ${imp.text}`}>
                    {c.impact_level}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.emerging_practices?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Emerging Practices</h4>
          <div className="space-y-2">
            {data.emerging_practices.map((p, i) => (
              <div key={i} className="bg-app-surface border border-app-positive rounded-lg p-3 flex items-start gap-2">
                <TrendingUp className="w-4 h-4 text-app-positive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-app-ink">{p}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.deprecated_advice?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Deprecated Advice</h4>
          <div className="space-y-2">
            {data.deprecated_advice.map((d, i) => (
              <div key={i} className="bg-app-surface border border-app-critical rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Trash2 className="w-4 h-4 text-app-critical mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-app-ink line-through opacity-60">{d.advice}</p>
                    <p className="text-xs text-app-muted mt-1"><span className="font-medium">Reason:</span> {d.reason}</p>
                    {d.replacement && (
                      <p className="text-xs text-app-positive mt-0.5"><span className="font-medium">Replaced by:</span> {d.replacement}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
